import { memo, useCallback, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlockWrapper, CopyCode, Label, MessageWrapper, TypingDots } from './Message.styles';

interface Props {
  role: 'user' | 'assistant';
  content?: string;
  isThinking?: boolean;
  isCopied?: boolean;
  onCopy?: (text: string) => void;
}

const MessageBase = ({ role, content, isThinking, isCopied, onCopy }: Props) => {
  const { t } = useTranslation();
  const isUser = role === 'user';

  const handleCopyToClipboard = useCallback(
    (node?: ExtraProps['node']) => (e: PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const code = node?.children?.[0];
      if (code && 'children' in code) {
        const textContent = (code.children as unknown as Array<{ value?: string }>).map((child) => ('value' in child ? child.value : '')).join('');
        onCopy?.(textContent);
      }
    },
    [onCopy]
  );

  if (isThinking) {
    return (
      <MessageWrapper isUser={false}>
        <Label>{t('user.chat.assistantLabel')} </Label>
        <span style={{ marginLeft: '4px', opacity: 0.9 }}>{t('user.chat.thinkingAnalyzingDocs')}</span>
        <TypingDots>
          <span />
          <span />
          <span />
        </TypingDots>
      </MessageWrapper>
    );
  }

  return (
    <MessageWrapper isUser={isUser}>
      {isUser ? (
        <>
          <Label>{t('user.chat.youLabel')} </Label>
          {content}
        </>
      ) : (
        <>
          <Label>{t('user.chat.assistantLabel')} </Label>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
              strong: ({ node, ...props }) => <Label {...props} />,
              pre: ({ node, children, ...props }) => (
                <CodeBlockWrapper>
                  <pre {...props}>{children}</pre>
                  {onCopy && (
                    <CopyCode onPointerDown={handleCopyToClipboard(node)} title={isCopied ? t('user.chat.copied') : t('user.chat.copyText')}>
                      {isCopied ? '✓' : '📋'}
                    </CopyCode>
                  )}
                </CodeBlockWrapper>
              ),
            }}>
            {content || ''}
          </ReactMarkdown>
        </>
      )}
    </MessageWrapper>
  );
};

export const Message = memo(MessageBase);
